import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/pages/layoutChrome";
import { Button } from "@/components/ui/button";
import LicenseCommercialSection from "@/components/LicenseCommercialSection";
import { CONFIGURACION_DESPACHO_PATH } from "@/modules/config/configuracionRoutes";
import { VISOR_MODULE_BREADCRUMB_ROOT } from "@/modules/workModuleCore";
import { invalidateEntitlementsCache } from "@/modules/useEntitlements";

/**
 * Vista limpia de solicitud de licencia (deep link desde panel / Mi Negocio).
 */
export default function LicenseCommercialFocusView() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const load = useCallback(async () => {
    setLoadError("");
    setLoading(true);
    try {
      const { data } = await api.get("/system/info");
      setInfo(data);
    } catch (e) {
      setLoadError(formatApiError(e.response?.data?.detail) || "No se pudo cargar la información de licencia.");
      setInfo(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onLicenseInstalled = ({ info: newInfo }) => {
    if (newInfo) setInfo(newInfo);
    invalidateEntitlementsCache();
    load();
  };

  return (
    <>
      <PageHeader
        title="Mi Negocio / Solicitud de licencia"
        subtitle="Seleccione su plan, facturación y el método para enviar su solicitud a BNT."
        breadcrumb={[VISOR_MODULE_BREADCRUMB_ROOT, "Solicitud de licencia"]}
      />
      <div className="p-8 space-y-6 max-w-5xl" data-testid="license-commercial-focus-page">
        <div className="flex flex-wrap items-center gap-2">
          <Button asChild variant="ghost" size="sm" className="h-8 text-xs text-slate-700">
            <Link to="/" data-testid="license-focus-back-panel">
              <ArrowLeft className="w-3.5 h-3.5 mr-1" />
              Volver al panel
            </Link>
          </Button>
          <Button asChild variant="outline" size="sm" className="h-8 text-xs border-slate-300">
            <Link to={CONFIGURACION_DESPACHO_PATH} data-testid="license-focus-full-config-link">
              Ver datos completos del negocio
            </Link>
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-sm text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Cargando solicitud de licencia…
          </div>
        ) : null}

        {!loading && loadError ? (
          <div className="rounded-sm border border-amber-300 bg-amber-50 px-4 py-6 text-sm text-amber-950 space-y-3">
            <p className="font-semibold m-0">No se pudo cargar esta pantalla</p>
            <p className="text-xs m-0">{loadError}</p>
            <Button type="button" variant="outline" size="sm" onClick={load}>
              Reintentar
            </Button>
          </div>
        ) : null}

        {!loading && info ? (
          <div className="rounded-sm border border-slate-200 bg-white shadow-sm p-4 md:p-5">
            <LicenseCommercialSection
              systemInfo={info}
              profileDefaults={info.profile}
              onLicenseInstalled={onLicenseInstalled}
              focusMode
            />
          </div>
        ) : null}
      </div>
    </>
  );
}
